import { useNavigate, useParams, Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';
import MangaCard from '@/components/base/MangaCard';
import Seo from '@/components/base/Seo';
import { categories } from '@/mocks/categories';
import { mangas } from '@/mocks/mangas';

const sortOptions = [
  { id: 'hot', label: '热度优先' },
  { id: 'rating', label: '评分最高' },
  { id: 'update', label: '最近更新' },
];

export default function Category() {
  const { id = 'rexue' } = useParams();
  const navigate = useNavigate();
  const [sort, setSort] = useState('hot');
  const [status, setStatus] = useState<'all' | '连载中' | '已完结'>('all');

  const active = categories.find((c) => c.id === id) || categories[0];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const list = useMemo(() => {
    let base = mangas.filter((m) => m.categoryId === active.id);
    if (status !== 'all') base = base.filter((m) => m.status === status);
    const sorted = [...base];
    if (sort === 'rating') sorted.sort((a, b) => b.rating - a.rating);
    else if (sort === 'update') sorted.sort((a, b) => b.totalChapters - a.totalChapters);
    else sorted.sort((a, b) => b.followers - a.followers);
    return sorted;
  }, [active.id, sort, status]);

  return (
    <>
      <Seo
        title={`${active.name}漫画 - 热门${active.name}连载推荐 | 漫迹漫画追更工具`}
        description={`漫迹${active.name}分类，为您推荐热门${active.name}漫画。支持漫画更新提醒、最新话推送与连载进度查询，追更${active.name}不迷路。`}
        path={`/category/${active.id}`}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mangpush.github.io/' },
              { '@type': 'ListItem', position: 2, name: active.name },
            ],
          },
        ]}
      />
      <Navbar />

      <main className="mx-auto max-w-7xl px-4 pt-24 md:px-6 md:pt-28">
        {/* 面包屑 */}
        <nav aria-label="面包屑" className="mb-6 flex items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/category/rexue" className="hover:text-primary-600">漫画分类</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="font-medium text-foreground-800">{active.name}</span>
        </nav>

        {/* 分类切换 */}
        <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => navigate(`/category/${c.id}`)}
              className={`flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                c.id === active.id
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              <i className={c.icon} />
              {c.name}
            </button>
          ))}
        </div>

        {/* 分类头部 */}
        <div className="rounded-2xl bg-background-100 px-6 py-8 md:px-8">
          <h1 className="font-heading text-3xl text-foreground-950">{active.name}漫画</h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">{active.desc}</p>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="inline-flex items-center gap-1"><i className="ri-book-2-line" /> 共 {active.count} 部作品</span>
            <span className="inline-flex items-center gap-1"><i className="ri-user-follow-line" /> 本站共收录 {active.count * 2400} 位追更读者</span>
          </div>
        </div>

        {/* 筛选 */}
        <div className="mt-6 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2">
            {(['all', '连载中', '已完结'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  status === s ? 'bg-secondary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {s === 'all' ? '全部' : s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {sortOptions.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setSort(o.id)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  sort === o.id ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* 漫画列表 */}
        {list.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 pb-16 sm:grid-cols-2 lg:grid-cols-4">
            {list.map((m) => (
              <MangaCard key={m.id} manga={m} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center gap-3 pb-20 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-inbox-line" />
            </span>
            <p className="text-sm text-foreground-500">该分类下暂无符合条件的漫画</p>
          </div>
        )}
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}